import React, { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { object, string, ValidationError } from "yup";
import {
  countryOptions,
  User,
  BillingFormValues,
  InputVariantTypes,
  Country,
} from "@ecologi/shared";
import {
  fetchPlacesAutocomplete,
  fetchAndParsePlace,
} from "../utils/google-places";
import styled from "@emotion/styled";
import { TextField } from "../TextField";
import { Select } from "../Select";
import { Button } from "../Button";
import { FormLayout, RowItems, RowItem } from "../FormLayout/FormLayout.styles";
import { InlineBanner } from "../InlineBanner";
import { useForm } from "../utils/";
import isEqual from "lodash/isEqual";
import { AutoComplete } from "../AutoComplete";

export type BillingDetailsUpdatesArgs = {
  userId: string;
  fieldsToUpdate: BillingFormValues;
};

export type SubmitBillingDetailsFn = (
  details: BillingDetailsUpdatesArgs
) => Promise<any>;

type ExistingUserProps = {
  user?: User;
  onSubmitBillingDetails?: SubmitBillingDetailsFn;
};

type GuestCheckoutProps = {
  initialValues?: Partial<BillingFormValues>;
  onGuestSubmit?: (values: BillingFormValues) => Promise<void>;
  requireBillingAddress?: boolean;
};

type UICustomizationProps = {
  submitButtonText?: string;
  actionType?: "primary" | "secondary";
  inputVariant?: InputVariantTypes;
  buttonPlacement?: "left" | "right";
  showCancel?: boolean;
  onCancel?: () => void;
};

type Props = {
  errorMessage?: string | null;
  onSuccessfulSubmit?: () => void;
} & ExistingUserProps &
  GuestCheckoutProps &
  UICustomizationProps;

const ButtonContainer = styled.div<{
  buttonPlacement: "left" | "right" | "spread";
}>`
  display: flex;
  justify-content: ${({ buttonPlacement }) =>
    buttonPlacement === "right"
      ? "flex-end"
      : buttonPlacement === "left"
      ? "flex-start"
      : "space-between"};
  margin-top: 32px;
  margin-bottom: 20px;
`;

const ManualEntryLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-top: 8px;
  color: ${({ theme }) => theme.colors.charcoal};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    text-decoration: none;
  }
`;

const schema = (requireCompanyName: boolean, requireBillingAddress: boolean) =>
  object()
    .shape({
      billingCompanyName: requireCompanyName
        ? string().default("").min(1, "Please enter a Company Name.").required()
        : string(),
      billingEmail: string()
        .default("")
        .email("Please enter a valid email address."),
      billingAddress1: string().default(""),
      billingAddress2: string(),
      billingCountry: string(),
      billingCity: string().default(""),
      billingPostcode: string().default(""),
    })
    .test("address-validation", "", function (values) {
      const { billingAddress1, billingCity, billingPostcode } = values;

      const hasAnyAddressText = Boolean(
        billingAddress1?.trim() ||
          billingCity?.trim() ||
          billingPostcode?.trim()
      );

      // For guest checkout, always require address fields
      // For existing users, only validate if they've started filling in address
      if (!hasAnyAddressText && !requireBillingAddress) return true;

      const errors: ValidationError[] = [];

      if (!billingAddress1?.trim()) {
        errors.push(
          this.createError({
            path: "billingAddress1",
            message: "Please enter the first line of your address.",
          })
        );
      }
      if (!billingCity?.trim()) {
        errors.push(
          this.createError({
            path: "billingCity",
            message: "Please enter your city.",
          })
        );
      }
      if (!billingPostcode?.trim()) {
        errors.push(
          this.createError({
            path: "billingPostcode",
            message: "Please enter your postcode.",
          })
        );
      }

      if (errors.length > 0) {
        throw new ValidationError(errors);
      }

      return true;
    });

export const BillingDetailsForm: React.FC<Props> = ({
  errorMessage,
  user,
  onSubmitBillingDetails,
  initialValues,
  onGuestSubmit,
  requireBillingAddress = false,
  submitButtonText = "Save",
  actionType = "secondary",
  inputVariant = InputVariantTypes.DEFAULT,
  onSuccessfulSubmit,
  buttonPlacement = "left",
  showCancel = false,
  onCancel,
}) => {
  const defaultValues: BillingFormValues = user
    ? {
        billingCompanyName: user.billingCompanyName,
        billingEmail: user.billingEmail,
        billingAddress1: user.billingAddress1,
        billingAddress2: user.billingAddress2,
        billingCity: user.billingCity,
        billingPostcode: user.billingPostcode,
        billingCountry: user.billingLocation,
      }
    : {
        billingCompanyName: initialValues?.billingCompanyName ?? "",
        billingEmail: initialValues?.billingEmail ?? "",
        billingAddress1: initialValues?.billingAddress1 ?? "",
        billingAddress2: initialValues?.billingAddress2 ?? "",
        billingCity: initialValues?.billingCity ?? "",
        billingPostcode: initialValues?.billingPostcode ?? "",
        billingCountry: initialValues?.billingCountry ?? "",
      };

  const handleFormSubmit = async (formValues: BillingFormValues) => {
    if (user && onSubmitBillingDetails) {
      await onSubmitBillingDetails({
        userId: user.userId,
        fieldsToUpdate: formValues,
      });
    } else if (onGuestSubmit) {
      await onGuestSubmit(formValues);
    }

    onSuccessfulSubmit?.();
  };

  const form = useForm<BillingFormValues>({
    validationSchema: schema(!!user?.isBusinessType, requireBillingAddress),
    initialValues: defaultValues,
    onSubmit: handleFormSubmit,
  });

  const {
    isSubmitting,
    handleSubmit,
    isValid,
    values,
    errors: {
      billingCompanyName,
      billingAddress1,
      billingCity,
      billingPostcode,
      billingEmail,
    },
  } = form;

  const hasExistingAddress = Boolean(
    defaultValues.billingAddress1 ||
      defaultValues.billingCity ||
      defaultValues.billingPostcode
  );

  const [showAddressFields, setShowAddressFields] =
    useState(hasExistingAddress);
  const [finderInput, setFinderInput] = useState("");
  const [finderSelectedOption, setFinderSelectedOption] = useState({
    value: "",
    label: "",
  });

  const MANUAL_ENTRY_KEY = "MANUAL_ENTRY";

  const fetchOptions = useCallback(
    async ({ query, limit }: { query: string; limit: number }) => {
      const manualEntryOption = {
        key: MANUAL_ENTRY_KEY,
        value: MANUAL_ENTRY_KEY,
        label: "Enter address manually",
        skipInputUpdate: true,
      };

      try {
        const suggestions = await fetchPlacesAutocomplete(
          query,
          values.billingCountry as Country
        );
        const googleOptions = suggestions.slice(0, limit).map((s) => ({
          key: s.placeId,
          value: s.placeId,
          label: s.description,
          skipInputUpdate: true,
        }));

        return [...googleOptions, manualEntryOption];
      } catch {
        return [manualEntryOption];
      }
    },
    [values.billingCountry]
  );

  const handlePlaceSelection = async (placeId: string) => {
    if (placeId === MANUAL_ENTRY_KEY) {
      setShowAddressFields(true);
      setFinderInput("");
      setFinderSelectedOption({ value: "", label: "" });
      return;
    }

    try {
      const address = await fetchAndParsePlace(placeId);
      form.setValues(
        {
          ...form.values,
          ...address,
        },
        true
      );
    } catch {
      toast.error(
        "Unable to retrieve address details. Please enter your address manually."
      );
    }

    setShowAddressFields(true);
    setFinderInput("");
    setFinderSelectedOption({ value: "", label: "" });
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <FormLayout>
          {user?.isBusinessType && (
            <RowItem>
              <TextField
                {...form.getFieldProps("billingCompanyName", "Company name")}
                isDisabled={isSubmitting}
                maxLength={100}
                errorText={billingCompanyName?.toString()}
                variant={inputVariant}
              />
            </RowItem>
          )}

          <RowItem>
            <Select
              {...form.getFieldProps("billingCountry", "Country")}
              items={countryOptions}
              isDisabled={isSubmitting}
              variant={inputVariant}
            />
          </RowItem>

          <RowItem>
            <AutoComplete
              name="addressFinder"
              label="Address finder"
              placeholder="Start typing the first line of your address"
              input={finderInput}
              onInputChange={setFinderInput}
              selectedOption={finderSelectedOption}
              fetchOptions={fetchOptions}
              onSelectOption={handlePlaceSelection}
              variant={inputVariant}
              isDisabled={isSubmitting}
              showGoogleAttribution
              blurOnSelect
              settings={{
                debounceDelay: 300,
                minSearchLength: 3,
                fetchLimit: 10,
              }}
            />
            {!showAddressFields && (
              <ManualEntryLink
                type="button"
                onClick={() => setShowAddressFields(true)}
                data-testid="enter-address-manually-link"
              >
                Enter address manually
              </ManualEntryLink>
            )}
          </RowItem>

          {showAddressFields && (
            <>
              <RowItem>
                <TextField
                  {...form.getFieldProps("billingAddress1", "Address line 1")}
                  isDisabled={isSubmitting}
                  maxLength={100}
                  errorText={billingAddress1?.toString()}
                  variant={inputVariant}
                />
              </RowItem>

              <RowItem>
                <TextField
                  {...form.getFieldProps(
                    "billingAddress2",
                    "Address line 2 (optional)"
                  )}
                  isDisabled={isSubmitting}
                  maxLength={100}
                  variant={inputVariant}
                />
              </RowItem>

              <RowItems>
                <RowItem>
                  <TextField
                    {...form.getFieldProps("billingCity", "City")}
                    isDisabled={isSubmitting}
                    maxLength={100}
                    errorText={billingCity?.toString()}
                    variant={inputVariant}
                  />
                </RowItem>
                <RowItem>
                  <TextField
                    {...form.getFieldProps("billingPostcode", "Postcode")}
                    isDisabled={isSubmitting}
                    maxLength={100}
                    errorText={billingPostcode?.toString()}
                    variant={inputVariant}
                  />
                </RowItem>
              </RowItems>
            </>
          )}
          {!!user?.isBusinessType && (
            <RowItem>
              <TextField
                {...form.getFieldProps("billingEmail", "Billing email address")}
                type="email"
                isDisabled={isSubmitting}
                maxLength={100}
                helpText="Your invoices will be sent to this address "
                placeholder="accounts@example.com"
                errorText={billingEmail?.toString()}
                variant={inputVariant}
              />
            </RowItem>
          )}
        </FormLayout>
        {errorMessage && (
          <div style={{ margin: "16px 0px" }}>
            <InlineBanner title="Ooops!" status="critical" onDismiss={() => {}}>
              <p>{errorMessage}</p>
            </InlineBanner>
          </div>
        )}
        <ButtonContainer
          buttonPlacement={showCancel ? "spread" : buttonPlacement}
        >
          {showCancel && (
            <Button
              textOnlyWithBorder
              disabled={isSubmitting}
              text="Cancel"
              onClick={onCancel}
            />
          )}
          <Button
            loading={isSubmitting}
            disabled={
              !isValid ||
              isSubmitting ||
              (user && isEqual(defaultValues, values)) ||
              (requireBillingAddress &&
                (!values.billingAddress1?.trim() ||
                  !values.billingCity?.trim() ||
                  !values.billingPostcode?.trim()))
            }
            submit
            darkGreen={actionType === "secondary"}
            primary={actionType === "primary"}
            text={submitButtonText}
            data-testid="save-billing-details-button"
          />
        </ButtonContainer>
      </form>
    </div>
  );
};
